import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import ExpiryDatePicker from './ExpiryDatePicker';
import { PlusIcon, DocumentTextIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface MeetingNote {
  id: string;
  title: string;
  date: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  lastModified: string;
}

const MeetingNotesManager: React.FC = () => {
  const [meetingNotes, setMeetingNotes] = useState<MeetingNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<MeetingNote | null>(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    file: null as File | null,
    expiryDate: ''
  });

  useEffect(() => {
    loadMeetingNotes();
  }, []);

  const loadMeetingNotes = async () => {
    setLoading(true);
    try {
      const data = await apiService.getMeetingNotes();
      const notes = Array.isArray(data) ? data.filter(item => item !== null) as unknown as MeetingNote[] : [];
      setMeetingNotes(notes);
    } catch (error) {
      console.error('Failed to load meeting notes:', error);
      setMeetingNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a PDF or Word document');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.title || !uploadForm.date) {
      alert('Please fill in all required fields and select a file');
      return;
    }

    setUploading(true);
    try {
      await apiService.uploadMeetingNote(
        uploadForm.file,
        uploadForm.title,
        uploadForm.description,
        uploadForm.date,
        uploadForm.expiryDate || undefined
      );

      // Reset form
      setUploadForm({
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        file: null,
        expiryDate: ''
      });
      
      // Clear file input
      const fileInput = document.getElementById('meeting-note-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Reload meeting notes
      await loadMeetingNotes();
      
      alert('Meeting note uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload meeting note. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (note: MeetingNote) => {
    setDeleteConfirm(note);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await apiService.deleteMeetingNote(deleteConfirm.id);
      await loadMeetingNotes();
      setDeleteConfirm(null);
      alert('Meeting note deleted successfully');
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete meeting note. Please try again.');
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeLabel = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf': return 'PDF';
      case 'doc': return 'Word Doc';
      case 'docx': return 'Word Doc';
      default: return 'Document';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Upload Meeting Notes
        </h3>
        
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                placeholder="e.g., Parish Council Meeting - March 2024"
                required
              />
            </div>
            
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Meeting Date *
              </label>
              <input
                type="date"
                id="date"
                value={uploadForm.date}
                onChange={(e) => setUploadForm(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                required
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={uploadForm.description}
              onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={3}
              placeholder="Optional description of the meeting content"
            />
          </div>
          
          <div>
            <label htmlFor="meeting-note-file" className="block text-sm font-medium text-gray-700 mb-1">
              Document File * (PDF or Word, Max 10MB)
            </label>
            <input
              type="file"
              id="meeting-note-file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
            {uploadForm.file && (
              <p className="text-sm text-gray-600 mt-1">
                Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)}) - {getFileTypeLabel(uploadForm.file.name)}
              </p>
            )}
          </div>

          <ExpiryDatePicker
            value={uploadForm.expiryDate}
            onChange={(expiryDate) => setUploadForm(prev => ({ ...prev, expiryDate }))}
          />

          <button
            type="submit"
            disabled={uploading}
            className="bg-church-blue text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-church-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Upload Meeting Notes'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DocumentTextIcon className="h-5 w-5 mr-2" />
          Existing Meeting Notes
        </h3>
        
        {loading ? (
          <p className="text-gray-600">Loading meeting notes...</p>
        ) : meetingNotes.length === 0 ? (
          <p className="text-gray-600">No meeting notes uploaded yet.</p>
        ) : (
          <div className="space-y-3">
            {meetingNotes.map((note) => (
              <div key={note.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{note.title}</h4>
                  <p className="text-sm text-gray-600">
                    {note.fileName} • {formatFileSize(note.fileSize)} • {getFileTypeLabel(note.fileName)} • 
                    {new Date(note.date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <a
                    href={note.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-church-blue hover:text-blue-700 text-sm font-medium"
                  >
                    View Document
                  </a>
                  <button
                    onClick={() => handleDelete(note)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Delete meeting note"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <XMarkIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "{deleteConfirm.title}"? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                Delete
              </button>
              <button
                onClick={cancelDelete}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingNotesManager;